import { useEffect, useState, useRef, useContext, useMemo } from "react";
import CounterTrafficChart from "./charts/CounterTrafficChart";
import ConversionOccupancyChart from "./charts/ConversionOccupancyChart";
import { Line } from "react-chartjs-2";
import "chart.js/auto";
import "chartjs-adapter-moment";
import zoomPlugin from "chartjs-plugin-zoom";
import annotationPlugin from 'chartjs-plugin-annotation';
import { Chart as ChartJS } from "chart.js";
import AuthContext from "../../contexts/auth-context";
import React from 'react';
import Icon from "../../components/atoms/Icon";
import { Panel, PanelHeader, InfoTip } from "../Concession/components/ConcessionUI";
import { ChartHeader, ZoomControls, applyChartTheme, CHART_THEME, sectionLineDataset, hoverGuidePlugin } from "../Concession/charts/chartTheme";
import "../Concession/concession.css";
import "./retail.css";
import { PHASES, PURCHASE_COLOR, DATA_NEUTRAL, DATA_REST } from "./retailPalette";

ChartJS.register(zoomPlugin, annotationPlugin);

// ============================================================================
// PRESENTATION (logic for counts, conversion and stairs is unchanged)
// ============================================================================

const fmt = (n) => (n || 0).toLocaleString();

// Helper functions for time formatting
function convertTo12Hour(time) {
  if (!time) return " ";

  const [hours, minutes] = time.split(":");
  const parsedHours = parseInt(hours);
  const ampm = parsedHours >= 12 ? "PM" : "AM";
  const displayHours = parsedHours % 12 || 12;

  return `${displayHours}:${minutes} ${ampm}`;
}

// One bar split into parts, each with a label, count and share
const SplitBar = ({ parts }) => {
  const total = parts.reduce((sum, p) => sum + (p.value || 0), 0);
  return (
    <div className="rx-split">
      <div className="rx-split-bar" role="img" aria-label={parts.map((p) => `${p.label} ${fmt(p.value)}`).join(', ')}>
        {parts.map((p) => (
          <div
            key={p.key}
            className="rx-split-seg"
            style={{ width: `${total ? (p.value / total) * 100 : 0}%`, background: p.color }}
            title={`${p.label}: ${fmt(p.value)}`}
          />
        ))}
      </div>
      <div className="rx-split-tiles">
        {parts.map((p) => (
          <div
            key={p.key}
            className={`rx-split-tile ${p.phase ? 'rx-split-tile-phase' : ''}`}
            style={p.phase ? { background: p.tint, borderLeftColor: p.color } : undefined}
          >
            <div className="rx-split-head">
              <span className="rx-split-swatch" style={{ background: p.color }} />
              <span className="rx-split-label">{p.label}</span>
              {p.range && <span className="rx-split-range">{p.range}</span>}
            </div>
            <div className="rx-split-value">{fmt(p.value)}</div>
            <div className="rx-split-meta">
              {total ? `${Math.round((p.value / total) * 100)}%` : '0%'}
              {p.meta ? ` · ${p.meta}` : ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Tiles for individual sensors (doors, registers), with share of a total
const SensorTiles = ({ items, total }) => {
  const max = Math.max(1, ...items.map((i) => i.value || 0));
  return (
    <div className="rx-sensor-grid">
      {items.map((i) => (
        <div key={i.key} className="rx-sensor cx-fade-in">
          <div className="rx-sensor-top">
            <span className="rx-sensor-label">{i.label}</span>
            {i.id !== undefined && <span className="rx-sensor-id">ID {i.id}</span>}
          </div>
          <div className="rx-sensor-value">{fmt(i.value)}</div>
          <div className="rx-sensor-bar"><span style={{ width: `${((i.value || 0) / max) * 100}%`, background: i.color || DATA_NEUTRAL }} /></div>
          {total > 0 && <div className="rx-sensor-meta">{Math.round(((i.value || 0) / total) * 100)}% of traffic</div>}
        </div>
      ))}
    </div>
  );
};

const convertGameStartTimeToAnnotation = (gameDate, gameStartTime) => {
  if (!gameDate || !gameStartTime) return null;
  
  const [hours, minutes] = gameStartTime.split(':').map(Number);
  const today = new Date();
  const baseTime = new Date(today);
  baseTime.setHours(8, 0, 0, 0);
  
  let businessMinutes;
  if (hours >= 8) {
    businessMinutes = (hours - 8) * 60 + minutes;
  } else if (hours < 3) {
    businessMinutes = (16 * 60) + (hours * 60) + minutes;
  } else {
    businessMinutes = (19 * 60) + ((hours - 3) * 60) + minutes;
  }
  
  const annotationTime = new Date(baseTime.getTime() + (businessMinutes * 60 * 1000));
  return annotationTime.toISOString();
};

const StairsTrafficChart = ({ stairsTrafficDataSets, currentGame, dayType }) => {
  const authCtx = useContext(AuthContext);
  const [dataIn, setDataIn] = useState([]);
  const [dataOut, setDataOut] = useState([]);
  const [loading, setLoading] = useState(true);
  const chartRef = useRef(null);

  useEffect(() => {
    setLoading(true);

    const processedInData = [];
    const processedOutData = [];

    stairsTrafficDataSets.forEach((dataSet) => {
      const sortedDataSet = dataSet
        .filter(item => item.timestamp && item.timestamp !== "00:00")
        .map(item => {
          // Handle both ISO ("YYYY-MM-DDTHH:MM") and time-only ("HH:MM") timestamps
          const ts = item.timestamp;
          const timePart = ts.includes('T') ? ts.split('T')[1] : ts;
          if (!timePart || !timePart.includes(':')) return null;

          const [hours, minutes] = timePart.split(':').map(Number);
          if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

          let businessMinutes;
          if (hours >= 8) {
            businessMinutes = (hours - 8) * 60 + minutes;
          } else if (hours < 3) {
            businessMinutes = (16 * 60) + (hours * 60) + minutes;
          } else {
            businessMinutes = (19 * 60) + ((hours - 3) * 60) + minutes;
          }

          return { ...item, businessMinutes };
        })
        .filter(Boolean)
        .sort((a, b) => a.businessMinutes - b.businessMinutes);

      const processedIn = [];
      const processedOut = [];

      // No trimming — render every true data point as-is.
      sortedDataSet.forEach((item) => {
        const today = new Date();
        const baseTime = new Date(today);
        baseTime.setHours(8, 0, 0, 0);
        
        const chartTime = new Date(baseTime.getTime() + (item.businessMinutes * 60 * 1000));
        const isoDateStr = chartTime.toISOString();

        processedIn.push({
          x: isoDateStr,
          y: item.countIn || 0,
        });

        processedOut.push({
          x: isoDateStr,
          y: item.countOut || 0,
        });
      });

      processedInData.push(processedIn);
      processedOutData.push(processedOut);
    });

    setDataIn(processedInData);
    setDataOut(processedOutData);
    setLoading(false);
  }, [stairsTrafficDataSets]);

  const maxValue = Math.max(
    ...dataIn.flatMap((dataSet) => dataSet.map((item) => item.y)),
    ...dataOut.flatMap((dataSet) => dataSet.map((item) => item.y)),
    0
  );

  // Total number of points currently plotted. Used in the <Line> key so the
  // chart remounts (and re-fits its axes) when more data arrives after the
  // initial render — otherwise the zoom plugin keeps the originally captured
  // x-range and clips the late-arriving points.
  const pointSignature = useMemo(
    () => dataIn.reduce((sum, ds) => sum + ds.length, 0),
    [dataIn]
  );

  const gameStartAnnotation = useMemo(() => {
    if (dayType !== 'game') return null;
    return currentGame?.date && currentGame?.gameStartTime ? 
      convertGameStartTimeToAnnotation(currentGame.date, currentGame.gameStartTime) : null;
  }, [dayType, currentGame?.date, currentGame?.gameStartTime]);

  const primaryTextColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-txt').trim();

  const chartData = {
    labels: dataIn.length > 0 ? dataIn[0].map((item) => item.x) : [],
    datasets: [
      // In datasets
      ...dataIn.map((dataSet) => ({
        label: 'Going Up',
        data: dataSet.map((item) => ({ x: item.x, y: item.y })),
        ...sectionLineDataset("#16a34a"),
      })),
      // Out datasets
      ...dataOut.map((dataSet) => ({
        label: 'Going Down',
        data: dataSet.map((item) => ({ x: item.x, y: item.y })),
        ...sectionLineDataset("#dc2626"),
      })),
    ],
  };

  const chartOptions = useMemo(() => ({
    scales: {
      x: {
        title: {
          color: primaryTextColor,
          display: true,
          text: "Time",
          padding: { top: 0, bottom: 10 },
        },
        type: "time",
        time: {
          unit: "hour",
          displayFormats: {
            hour: "h A",
          },
        },
        grid: {
          display: false,
        },
        ticks: {
          source: "auto",
          color: primaryTextColor,
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
        },
      },
      y: {
        display: true,
        title: {
          color: primaryTextColor,
          display: true,
          text: "People",
          padding: { top: 0, bottom: 10 },
        },
        grid: {
          display: true,
        },
        ticks: {
          color: primaryTextColor,
          precision: 0,
          beginAtZero: true,
          stepSize: (maxValue < 100) ? 20 : 100,
          callback: function (value) {
            if (value % 100 === 0) {
              return value;
            }
          },
        },
        max: (maxValue < 100) ? 100 : Math.max(Math.ceil(maxValue / 100) * 100) + 100
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        callbacks: {
          title: (context) => {
            if (context.length === 0) {
              return "";
            } else {
              const labelParts = context[0].label.split(',');  
              const pulledTime = labelParts.length > 2 ? labelParts[2].trim() : '';

              const timeFormatter = new Date(`2000-01-01 ${pulledTime}`);
              const formattedTime = timeFormatter.toLocaleTimeString([], {
                hour: 'numeric',
                minute: '2-digit'
              });

              return `${formattedTime}`;
            }
          },
          label: (context) => ` ${context.dataset.label}: ${context.raw.y}`,
        },
      },
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: primaryTextColor,
          usePointStyle: true,
          pointStyle: 'rectRounded',
          generateLabels: function(chart) {
            const defaultLabels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
            
            if (gameStartAnnotation) {
              const gameStartLabel = {
                text: 'Game Start',//authCtx.league === "mlb" ? "First Pitch" : authCtx.league === "nfl" ? "Kick Off" : "Game Start",
                fillStyle: 'transparent',
                strokeStyle: CHART_THEME.gameStart,
                lineWidth: 3,
                lineDash: [8, 2],
                pointStyle: 'line',
                hidden: false,
                index: -1
              };
              
              return [gameStartLabel, ...defaultLabels];
            }
            
            return defaultLabels;
          }
        },
        onClick: function(evt, legendItem, legend) {
          if (legendItem.index !== -1) {
            ChartJS.defaults.plugins.legend.onClick.call(this, evt, legendItem, legend);
          }
        }
      },
      zoom: {
        limits: {
          x: { min: 'original', max: 'original', minRange: 60 * 60 * 1000 },
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true
          },
          mode: 'x',
        },
        pan: {
          enabled: true,
          mode: 'x',
        },
      },
      annotation: {
        annotations: gameStartAnnotation ? [
          {
            type: 'line',
            mode: 'vertical',
            scaleID: 'x',
            value: gameStartAnnotation,
            borderColor: CHART_THEME.gameStart,
            borderWidth: 2,
            borderDash: [8, 4]
          }
        ] : []
      },
    },
  }), [gameStartAnnotation, maxValue, primaryTextColor, authCtx.league]);

  if (!loading && dataIn.length > 0) {
    return (
      <div className="graph panel-2 cx-chart-card rx-nested-chart">
        <ChartHeader
          title={"Stairs over time"}
          subtitle={"People going up and down across the day."}
          controls={<ZoomControls chartRef={chartRef} />}
        />
        <div style={{ position: 'relative', height: '300px', marginTop: '8px' }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflowX: 'auto',
          }}>
            <div style={{ width: '100%', height: '100%' }}>
              <Line 
                key={`${currentGame?.date || 'no-game'}-${pointSignature}`} 
                ref={chartRef} 
                data={chartData} 
                options={applyChartTheme(chartOptions)} 
                plugins={[hoverGuidePlugin]}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <p>Loading...</p>;
};

// ============================================================================
// STAIRS COUNTERS SECTION
// ============================================================================

// Stairs: going up / down / net, plus the stairs chart
const StairsCountersSection = ({
  counterSensorInfoData,
  doorCounterSensorStairsIds,
  counterTrafficDataList,
  currentGame,
  dayType
}) => {
  const stairsCounters = Object.entries(counterSensorInfoData || {})
    .filter(([id, _]) => doorCounterSensorStairsIds.includes(parseInt(id)));

  // Calculate totals across all stairs sensors
  const totalIn = stairsCounters.reduce((sum, [, data]) => sum + (data.count || 0), 0);
  const totalOut = stairsCounters.reduce((sum, [, data]) => sum + (data.countOut || 0), 0);
  const netTraffic = totalIn - totalOut;

  // Build stairs-specific traffic data for the chart (memoized so the chart effect doesn't re-run every render)
  const stairsTrafficDataSets = useMemo(() => {
    return counterTrafficDataList.map(dataSet => {
      return dataSet.map(entry => {
        let stairsIn = 0;
        let stairsOut = 0;

        doorCounterSensorStairsIds.forEach(sensorId => {
          stairsIn += entry[String(sensorId)] || 0;
          stairsOut += entry[`${sensorId}_out`] || 0;
        });

        return {
          timestamp: entry.timestamp,
          countIn: stairsIn,
          countOut: stairsOut
        };
      });
    });
  }, [counterTrafficDataList, doorCounterSensorStairsIds]);

  if (stairsCounters.length === 0) {
    return null;
  }

  return (
    <Panel className="rx-counter-panel">
      <PanelHeader title="Stairs" subtitle="People going between floors today." />
      <div className="rx-stairs">
        <div className="rx-stairs-tile">
          <span className="rx-stairs-icon up"><Icon name="arrow-up" /></span>
          <div>
            <div className="cx-tile-detail-label">Going up</div>
            <div className="rx-split-value">{fmt(totalIn)}</div>
          </div>
        </div>
        <div className="rx-stairs-tile">
          <span className="rx-stairs-icon down"><Icon name="arrow-down" /></span>
          <div>
            <div className="cx-tile-detail-label">Going down</div>
            <div className="rx-split-value">{fmt(totalOut)}</div>
          </div>
        </div>
        <div className="rx-stairs-tile">
          <span className="rx-stairs-icon net"><Icon name="arrow-down-up" /></span>
          <div>
            <div className="cx-tile-detail-label">
              Net <InfoTip title="Net traffic" text="Difference between people going up and coming down the stairs." />
            </div>
            <div className="rx-split-value">{fmt(netTraffic)}</div>
          </div>
        </div>
      </div>
      <StairsTrafficChart
        stairsTrafficDataSets={stairsTrafficDataSets}
        currentGame={currentGame}
        dayType={dayType}
      />
    </Panel>
  );
};

// ============================================================================
// MAIN COUNTER COMPONENT
// ============================================================================

const Counter = ({
  dayType,
  isToday,
  currentLocation,
  currentGame,
  setAutoFetchEnabled,
  regularDayCount,
  counterDataAvailable,
  pregameCount,
  gatesOpenCount,
  firstPitchCount,
  counterSensorInfoData,
  counterTrafficDataList,
  occupancyData,
}) => {

  useEffect(() => {
    if (isToday) {
      setAutoFetchEnabled(true);
    }
  }, [isToday, setAutoFetchEnabled, counterSensorInfoData, currentLocation.doorCounterSensorSalesIds]);

  // No counter data for this date
  if (!counterDataAvailable) {
    return (
      <div className="cx-empty-state">
        <Icon name="door-open" />
        <div className="cx-empty-title">Entrance counts aren't available for this date</div>
        <div>Please try another date.</div>
      </div>
    );
  }

  // Ensure doorCounterSensorSalesIds and doorCounterSensorStairsIds are arrays
  const salesIds = currentLocation.doorCounterSensorSalesIds || [];
  const stairsIds = currentLocation.doorCounterSensorStairsIds || [];
  const isGame = dayType === 'game';

  // Traffic totals
  const gameTraffic = (gatesOpenCount || 0) + (firstPitchCount || 0);
  const dayTraffic = isGame ? (pregameCount || 0) + gameTraffic : (regularDayCount || 0);

  // Conversion: purchases (sales sensors' exits) over day traffic (same math as before)
  const showSales = salesIds.length > 0;
  const salesCounters = Object.entries(counterSensorInfoData || {})
    .filter(([id]) => salesIds.includes(parseInt(id)));
  const purchases = salesCounters.reduce((sum, [, d]) => sum + (d.countOut || 0), 0);
  const conversionBase = isGame ? (pregameCount || 0) + (gatesOpenCount || 0) + (firstPitchCount || 0) : (regularDayCount || 0);
  const noPurchase = Math.max(0, conversionBase - purchases);
  const conversionRate = conversionBase > 0 ? ((purchases / conversionBase) * 100).toFixed(1) : 0;

  // Doors (not sales, not stairs): game day only, as before
  const showDoors = isGame;
  const doors = Object.entries(counterSensorInfoData || {})
    .filter(([id]) => !salesIds.includes(parseInt(id)) && !stairsIds.includes(parseInt(id)))
    .map(([id, d]) => ({ key: id, id, label: d.label, value: d.count || 0 }))
    .sort((a, b) => b.value - a.value);
  const doorsTotal = doors.reduce((sum, d) => sum + d.value, 0);

  // Stairs
  const stairsCounters = Object.entries(counterSensorInfoData || {}).filter(([id]) => stairsIds.includes(parseInt(id)));
  const showStairs = isGame && stairsIds.length > 0 && stairsCounters.length > 0;

  return (
    <div className="rx-counter">
      {/* Traffic: the day's count, with the phase breakdown on game days */}
      <Panel className="rx-counter-panel">
        <div className={`rx-traffic-head${isGame ? ' rx-traffic-head-split' : ''}`}>
          {isGame && (
            <div className="rx-traffic-stat rx-traffic-stat-secondary">
              <span className="rx-traffic-label">
                Game traffic
                <InfoTip
                  title="Game traffic"
                  text="Everyone who entered the store from gates open onward, including after game start. Pregame entries are not included."
                />
              </span>
              <span className="rx-traffic-value">{fmt(gameTraffic)}</span>
              <span className="rx-traffic-meta">
                Since gates open{currentGame?.gatesOpen ? ` (${convertTo12Hour(currentGame.gatesOpen)})` : ''}
              </span>
            </div>
          )}
          <div className="rx-traffic-stat">
            <span className="rx-traffic-label">
              {isGame ? 'Day traffic' : 'Total traffic'}
              <InfoTip
                title={isGame ? 'Day traffic' : 'Total traffic'}
                text={isGame ? 'Everyone who entered the store today, including pregame.' : 'Everyone who entered the store today.'}
              />
            </span>
            <span className="rx-traffic-value">{fmt(dayTraffic)}</span>
            <span className="rx-traffic-meta">{isToday ? 'Live count' : 'Final count'}</span>
          </div>
        </div>
        {isGame && (
          <>
            <div className="rx-subhead">By phase</div>
            <SplitBar
              parts={[
                ...((pregameCount || 0) > 0 ? [{ key: 'pre', ...PHASES.pregame, phase: true, value: pregameCount, range: `Before ${convertTo12Hour(currentGame?.gatesOpen)}` }] : []),
                { key: 'gates', ...PHASES.gates, phase: true, value: gatesOpenCount || 0, range: `${convertTo12Hour(currentGame?.gatesOpen)} – ${convertTo12Hour(currentGame?.gameStartTime)}` },
                { key: 'start', ...PHASES.game, phase: true, value: firstPitchCount || 0, range: `From ${convertTo12Hour(currentGame?.gameStartTime)}` },
              ]}
            />
          </>
        )}
      </Panel>

      {/* Conversion */}
      {showSales && (
        <Panel className="rx-counter-panel">
          <PanelHeader
            title="Conversion"
            subtitle={`Visitors who were associated with a purchase, out of ${isGame ? 'day' : 'today\'s'} traffic.`}
            actions={
              <div className="rx-conversion-rate">
                <strong>{conversionRate}<span>%</span></strong>
                <small>{fmt(purchases)} of {fmt(conversionBase)} visitors</small>
              </div>
            }
          />
          {salesCounters.length > 0 ? (
            <>
              <SplitBar
                parts={[
                  { key: 'bought', label: 'Purchased', value: purchases, color: PURCHASE_COLOR },
                  { key: 'none', label: 'No purchase', value: noPurchase, color: DATA_REST },
                ]}
              />
              {/* Overlay only when the store also tracks occupancy */}
              {currentLocation.maxOccupancy > 0 && occupancyData && occupancyData.length > 0 && (
                <ConversionOccupancyChart
                  showPregame={(pregameCount || 0) > 0}
                  readings={counterTrafficDataList?.[0]}
                  salesIds={salesIds}
                  occupancy={occupancyData}
                  maxOccupancy={currentLocation.maxOccupancy}
                  currentGame={currentGame}
                  dayType={dayType}
                />
              )}
              {salesCounters.length > 1 && (
                <>
                  <div className="rx-subhead">By register</div>
                  <SensorTiles
                    items={salesCounters.map(([id, d]) => ({ key: id, id, label: d.label, value: d.countOut || 0 }))}
                    total={purchases}
                  />
                </>
              )}
            </>
          ) : (
            <div className="cx-empty">No conversion information available</div>
          )}
        </Panel>
      )}

      {/* Traffic over time, shaded by phase on game days */}
      <CounterTrafficChart
        trafficDataSets={counterTrafficDataList}
        currentGame={currentGame}
        dayType={dayType}
        showPregame={(pregameCount || 0) > 0}
        subtitle={isGame
          ? 'People entering the store, shaded by phase. Scroll to zoom, drag to pan.'
          : 'People entering the store across the day. Scroll to zoom, drag to pan.'}
      />

      {/* Entrances */}
      {showDoors && (
        <Panel className="rx-counter-panel">
          <PanelHeader title="Entrances" subtitle="Traffic through each entrance, busiest first." />
          {doors.length > 0 ? <SensorTiles items={doors} total={doorsTotal} /> : <div className="cx-empty">No counter information available</div>}
        </Panel>
      )}

      {/* Stairs (game day, when stairs sensors exist) */}
      {showStairs && (
        <StairsCountersSection
          counterSensorInfoData={counterSensorInfoData}
          doorCounterSensorStairsIds={stairsIds}
          counterTrafficDataList={counterTrafficDataList}
          currentGame={currentGame}
          dayType={dayType}
        />
      )}
    </div>
  );
}

export default React.memo(Counter, (prevProps, nextProps) => {
  // New occupancy readings redraw the conversion / occupancy chart
  if (prevProps.occupancyData !== nextProps.occupancyData) return false;
  if (!prevProps.counterDataAvailable && !nextProps.counterDataAvailable) {
    return true;
  }
  
  if (prevProps.dayType === 'regular' && nextProps.dayType === 'regular') {
    return prevProps.regularDayCount === nextProps.regularDayCount &&
      JSON.stringify(prevProps.counterSensorInfoData || {}) ===
      JSON.stringify(nextProps.counterSensorInfoData || {});
  }
  
  const prevSalesIds = JSON.stringify(prevProps.currentLocation?.doorCounterSensorSalesIds || []);
  const nextSalesIds = JSON.stringify(nextProps.currentLocation?.doorCounterSensorSalesIds || []);
  const prevStairsIds = JSON.stringify(prevProps.currentLocation?.doorCounterSensorStairsIds || []);
  const nextStairsIds = JSON.stringify(nextProps.currentLocation?.doorCounterSensorStairsIds || []);
  const prevSensorData = JSON.stringify(prevProps.counterSensorInfoData || {});
  const nextSensorData = JSON.stringify(nextProps.counterSensorInfoData || {});
  
  return (
    prevProps.pregameCount === nextProps.pregameCount &&
    prevProps.gatesOpenCount === nextProps.gatesOpenCount &&
    prevProps.firstPitchCount === nextProps.firstPitchCount &&
    prevProps.backgroundLoading === nextProps.backgroundLoading &&
    prevSalesIds === nextSalesIds &&
    prevStairsIds === nextStairsIds &&
    prevSensorData === nextSensorData
  );
});